import React, { useEffect, useState } from "react";
import { LEGACY_CONFIRMATION } from "../../utils/ilsLegacyUpdate";

function ProductsTable({ lines = [] }) {
  return (
    <div className="max-h-64 overflow-auto rounded border border-amber-200">
      <table className="min-w-full text-sm">
        <thead className="bg-amber-50 text-amber-900">
          <tr>
            <th className="px-3 py-2 text-left">Line Number</th>
            <th className="px-3 py-2 text-left">Product Code</th>
            <th className="px-3 py-2 text-right">Quantity</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={`${line.lineItemNumber}-${line.productCode}`} className="border-t border-amber-100">
              <td className="px-3 py-2">{line.lineItemNumber}</td>
              <td className="px-3 py-2 font-medium">{line.productCode}</td>
              <td className="px-3 py-2 text-right">{line.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LegacyIlsUpdateDialog({ preview, loading = false, onCancel, onExecute }) {
  const [accepted, setAccepted] = useState(false);
  const [confirmation, setConfirmation] = useState("");

  useEffect(() => {
    setAccepted(false);
    setConfirmation("");
  }, [preview?.attemptId]);

  if (!preview) return null;
  const executable = accepted && confirmation === LEGACY_CONFIRMATION && !loading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 max-h-[92vh] w-full max-w-3xl overflow-auto rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-xl font-semibold text-amber-900">{preview.warningTitle}</h2>
        <div className="mt-3 whitespace-pre-line rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
          {preview.warningBody}
        </div>
        <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
          <div><dt className="font-semibold">PO</dt><dd>{preview.po}</dd></div>
          <div><dt className="font-semibold">Outbound Code</dt><dd>{preview.outboundCode}</dd></div>
          <div><dt className="font-semibold">Current Order #3P</dt><dd>{preview.currentOrderNo3rdParty || "—"}</dd></div>
          <div><dt className="font-semibold">New Order #3P</dt><dd>{preview.proposedOrderNo3rdParty}</dd></div>
        </dl>
        <div className="mt-4"><ProductsTable lines={preview.lines} /></div>
        <label className="mt-4 flex items-start gap-2 text-sm font-medium text-slate-800">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
          />
          <span>I understand the risk and will verify all Products in iLS after the update.</span>
        </label>
        <label className="mt-4 block text-sm font-medium text-slate-800">
          Type <span className="font-mono">{LEGACY_CONFIRMATION}</span>
          <input
            aria-label="Legacy confirmation"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono"
          />
        </label>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onCancel} disabled={loading} className="rounded border px-4 py-2">
            Cancel
          </button>
          <button
            type="button"
            disabled={!executable}
            onClick={() => onExecute({ riskAccepted: accepted, confirmation })}
            className="rounded bg-amber-700 px-4 py-2 font-semibold text-white disabled:opacity-50"
          >
            {loading ? "Updating..." : "Update and Require Manual Verification"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function LegacyIlsPendingVerification({ attempt, loading = false, onVerified, onMismatch }) {
  const [mismatchMode, setMismatchMode] = useState(false);
  const [note, setNote] = useState("");
  if (!attempt) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-xl font-semibold">Manual iLS Products Verification</h2>
        <p className="mt-2 text-sm text-amber-800">
          Update sent. Manual iLS Products verification is required.
        </p>
        <p className="mt-3 text-sm">PO: <strong>{attempt.po}</strong></p>
        <p className="text-sm">Outbound Code: <strong>{attempt.outboundCode}</strong></p>
        <div className="mt-4"><ProductsTable lines={attempt.expectedLines} /></div>
        {mismatchMode && (
          <label className="mt-4 block text-sm font-medium">
            Describe the mismatch and confirm that you corrected or will correct the quantities in iLS.
            <textarea
              aria-label="Mismatch note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              className="mt-1 w-full rounded border px-3 py-2"
            />
          </label>
        )}
        <div className="mt-5 flex flex-wrap justify-end gap-3">
          {!mismatchMode && (
            <>
              <button type="button" disabled={loading} onClick={() => onVerified()} className="rounded bg-emerald-700 px-4 py-2 text-white">
                I Verified Products in iLS
              </button>
              <button type="button" disabled={loading} onClick={() => setMismatchMode(true)} className="rounded border border-rose-300 px-4 py-2 text-rose-700">
                Mismatch Found
              </button>
            </>
          )}
          {mismatchMode && (
            <>
              <button type="button" disabled={loading} onClick={() => setMismatchMode(false)} className="rounded border px-4 py-2">
                Cancel
              </button>
              <button
                type="button"
                disabled={loading || !note.trim()}
                onClick={() => onMismatch(note.trim())}
                className="rounded bg-rose-700 px-4 py-2 text-white disabled:opacity-50"
              >
                Record Mismatch
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
